import {
  Directive,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  AfterViewInit,
  Renderer2
} from '@angular/core';

/**
 * Excel-style column resize for HTML tables. Uses a dynamic <colgroup> so widths apply to thead/tbody/tfoot
 * even when rows use colspan. Enable with:
 * - `appTableColumnResize` on `<table>`, or
 * - class `table-resize-columns` on `<table>`
 *
 * Parent should allow horizontal overflow (e.g. `overflow-x-auto`) when columns exceed the viewport.
 */
@Directive({
  selector: 'table[appTableColumnResize], table.table-resize-columns',
  standalone: true
})
export class TableColumnResizeDirective implements AfterViewInit, OnDestroy {
  /** Minimum column width in pixels */
  @Input() resizeMinColumnWidth = 72;

  private readonly host: HTMLTableElement;
  private colgroup: HTMLTableColElement | null = null;
  private cols: HTMLTableColElement[] = [];
  private mutationObserver: MutationObserver | null = null;
  private syncScheduled = false;
  private activeDrag: {
    colIndex: number;
    startX: number;
    startWidth: number;
    moveUnlisten: () => void;
    upUnlisten: () => void;
  } | null = null;
  private readonly handleClass = 'table-column-resize-handle';
  private lastColumnCount = -1;
  /** True after the user manually resizes at least once (we stop auto-measuring widths). */
  private manualResized = false;
  /** Throttle auto-measuring to avoid expensive reflows. */
  private lastAutoMeasureTs = 0;

  constructor(
    private readonly el: ElementRef<HTMLTableElement>,
    private readonly r2: Renderer2,
    private readonly zone: NgZone
  ) {
    this.host = this.el.nativeElement;
  }

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => {
      queueMicrotask(() => this.scheduleSync());
      const thead = this.host.querySelector('thead');
      if (thead) {
        this.mutationObserver = new MutationObserver(() => this.scheduleSync());
        this.mutationObserver.observe(thead, { childList: true, subtree: true });
      }
    });
  }

  ngOnDestroy(): void {
    this.cancelDrag();
    this.mutationObserver?.disconnect();
    this.mutationObserver = null;
    this.removeHandles();
    if (this.colgroup?.parentNode === this.host) {
      this.host.removeChild(this.colgroup);
    }
    this.colgroup = null;
    this.cols = [];
  }

  private scheduleSync(): void {
    if (this.syncScheduled) return;
    this.syncScheduled = true;
    requestAnimationFrame(() => {
      this.syncScheduled = false;
      this.sync();
    });
  }

  private sync(): void {
    const thead = this.host.querySelector('thead');
    if (!thead) {
      this.removeHandles();
      return;
    }

    // Use the last header row as the "leaf" column row.
    // Grouped headers can add a first row with colspans; resizing/sizing must target
    // the actual per-column labels (last row) to keep widths aligned with tbody/tfoot.
    const headerRows = Array.from(thead.querySelectorAll(':scope > tr')) as HTMLTableRowElement[];
    const headerRow = headerRows.length > 0 ? headerRows[headerRows.length - 1] : null;
    if (!headerRow) {
      this.removeHandles();
      return;
    }

    const thList = Array.from(headerRow.querySelectorAll(':scope > th')) as HTMLTableCellElement[];
    if (thList.length === 0) {
      this.removeHandles();
      return;
    }

    const count = thList.length;
    const countChanged = count !== this.lastColumnCount;
    this.lastColumnCount = count;

    this.ensureColgroup(count);

    const reorderActive = this.host.getAttribute('data-column-reorder-active') === 'true';
    // Normally we only remeasure when column count changes (initial load / structure change),
    // so user drags (column resize) are kept.
    //
    // But when column drag-reordering is active, we remeasure so widths stay aligned with the moved columns.
    const shouldAutoMeasureForText = !this.manualResized && !countChanged && !reorderActive;
    const now = Date.now();
    const throttledAutoMeasure = shouldAutoMeasureForText && (now - this.lastAutoMeasureTs > 1000);

    if (countChanged || reorderActive || throttledAutoMeasure) {
      this.lastAutoMeasureTs = now;
      this.applyAutoMeasuredWidths(thList);
    }

    this.host.style.tableLayout = 'fixed';

    this.removeHandles();
    thList.forEach((th, index) => {
      const thEl = th as HTMLTableCellElement;
      if (!thEl.querySelector(`.${this.handleClass}`)) {
        const grip = this.r2.createElement('span');
        this.r2.addClass(grip, this.handleClass);
        this.r2.addClass(grip, 'no-print');
        this.r2.setAttribute(grip, 'role', 'separator');
        this.r2.setAttribute(grip, 'aria-orientation', 'vertical');
        this.r2.setAttribute(grip, 'title', 'Drag to resize column');
        this.r2.listen(grip, 'mousedown', (ev: MouseEvent) => this.onGripMouseDown(ev, index));
        this.r2.listen(grip, 'click', (ev: MouseEvent) => {
          ev.preventDefault();
          ev.stopPropagation();
        });
        this.r2.appendChild(thEl, grip);
      }
    });
  }

  /**
   * Measure header widths based on current text/content.
   * Implementation detail:
   * - Temporarily switch table layout to `auto`
   * - Temporarily clear explicit col widths
   * - Measure `th` widths
   * - Re-apply measured widths into the colgroup and return to `fixed`
   */
  private applyAutoMeasuredWidths(thList: HTMLTableCellElement[]): void {
    // Guard: only proceed when col elements are in place.
    if (!this.cols.length) return;

    const prevLayout = this.host.style.tableLayout;
    const scrollSnapshots = this.captureHorizontalScrollSnapshots();
    try {
      // Allow the browser to compute natural widths from content.
      this.host.style.tableLayout = 'auto';

      // Remove hard widths so columns can grow/shrink to fit content.
      this.cols.forEach((col) => {
        this.r2.setStyle(col, 'width', 'auto');
        this.r2.setStyle(col, 'min-width', '0px');
      });

      // Force reflow/layout before measurement.
      // Reading offsetHeight is a common way to force browser layout.
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      this.host.offsetHeight;

      thList.forEach((th, i) => {
        let w = th.getBoundingClientRect().width;
        if (w < 2) w = this.resizeMinColumnWidth;
        const widthPx = Math.max(this.resizeMinColumnWidth, Math.round(w * 100) / 100);
        this.applyColWidth(i, widthPx);
      });
    } finally {
      // Always restore fixed layout (directive expects fixed for stable resize).
      this.host.style.tableLayout = prevLayout || 'fixed';
      this.restoreHorizontalScrollSnapshots(scrollSnapshots);
    }
  }

  /**
   * Reflowing the table (auto layout + col widths) often resets scrollLeft on overflow-x parents
   * (e.g. Daily Roster). Capture any horizontally scrollable ancestor before layout mutations.
   */
  private captureHorizontalScrollSnapshots(): { el: HTMLElement; left: number }[] {
    const snaps: { el: HTMLElement; left: number }[] = [];
    let n: HTMLElement | null = this.host;
    while (n) {
      if (n.scrollWidth > n.clientWidth + 1) {
        snaps.push({ el: n, left: n.scrollLeft });
      }
      n = n.parentElement;
    }
    const root = document.documentElement;
    const body = document.body;
    if (root.scrollWidth > root.clientWidth + 1) {
      snaps.push({ el: root, left: root.scrollLeft });
    }
    if (body.scrollWidth > body.clientWidth + 1) {
      snaps.push({ el: body, left: body.scrollLeft });
    }
    return snaps;
  }

  private restoreHorizontalScrollSnapshots(snaps: { el: HTMLElement; left: number }[]): void {
    if (snaps.length === 0) return;
    const run = (): void => {
      for (const s of snaps) {
        try {
          if (s.el.isConnected) {
            s.el.scrollLeft = s.left;
          }
        } catch {
          /* ignore */
        }
      }
    };
    run();
    requestAnimationFrame(() => {
      run();
      requestAnimationFrame(run);
    });
  }

  private ensureColgroup(count: number): void {
    if (!this.colgroup) {
      this.colgroup = this.r2.createElement('colgroup');
      const caption = this.host.querySelector('caption');
      const ref: ChildNode | null = caption?.nextSibling ?? this.host.firstChild;
      if (ref) {
        this.r2.insertBefore(this.host, this.colgroup, ref);
      } else {
        this.r2.appendChild(this.host, this.colgroup);
      }
    }

    while (this.cols.length < count) {
      const col = this.r2.createElement('col') as HTMLTableColElement;
      this.r2.appendChild(this.colgroup!, col);
      this.cols.push(col);
    }
    while (this.cols.length > count) {
      const col = this.cols.pop();
      if (col?.parentNode) {
        this.r2.removeChild(this.colgroup!, col);
      }
    }
  }

  private applyColWidth(index: number, widthPx: number): void {
    const col = this.cols[index];
    if (!col) return;
    const w = Math.max(this.resizeMinColumnWidth, widthPx);
    this.r2.setStyle(col, 'width', `${w}px`);
    this.r2.setStyle(col, 'min-width', `${this.resizeMinColumnWidth}px`);
  }

  private removeHandles(): void {
    this.host.querySelectorAll(`.${this.handleClass}`).forEach((n) => n.remove());
  }

  private onGripMouseDown(event: MouseEvent, colIndex: number): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.activeDrag) this.cancelDrag();
    this.manualResized = true;

    const col = this.cols[colIndex];
    const startWidth = col?.getBoundingClientRect().width ?? this.resizeMinColumnWidth;

    const moveUnlisten = this.r2.listen('document', 'mousemove', (ev: MouseEvent) => {
      ev.preventDefault();
      const delta = ev.clientX - this.activeDrag!.startX;
      const next = Math.max(this.resizeMinColumnWidth, this.activeDrag!.startWidth + delta);
      this.applyColWidth(colIndex, next);
    });

    const upUnlisten = this.r2.listen('document', 'mouseup', () => {
      this.cancelDrag();
    });

    this.activeDrag = {
      colIndex,
      startX: event.clientX,
      startWidth,
      moveUnlisten,
      upUnlisten
    };

    this.r2.setStyle(document.body, 'cursor', 'col-resize');
    this.r2.setStyle(document.body, 'user-select', 'none');
  }

  private cancelDrag(): void {
    if (!this.activeDrag) return;
    this.activeDrag.moveUnlisten();
    this.activeDrag.upUnlisten();
    this.activeDrag = null;
    this.r2.removeStyle(document.body, 'cursor');
    this.r2.removeStyle(document.body, 'user-select');
  }
}
